import React, {ReactElement, ReactNode} from 'react';
import {EventType, MixpanelTrackEvent, trackEvent} from '../../../../analytics/tracking/tracking';
import {ObjectOfAny, RequireAtLeastOne} from '../../../../types';
import DynamicLink from '../../../DynamicLink/DynamicLink';

import './Button.scss';

export enum ButtonVariant {
  PRIMARY = 'primary',
  PRIMARY_OUTLINE = 'primary outline',
  CALL_TO_ACTION = 'call-to-action',
  CALL_TO_ACTION_OUTLINE = 'call-to-action outline',
  DEFAULT = 'default',
  DEFAULT_OUTLINE = 'default outline',
  DEFAULT_TEXT = 'default text',
  LINK_TEXT = 'link text',
  NEGATIVE = 'negative',
  SUCCESS = 'success',
  UNSTYLED = 'unstyled',
  // @todo make sure it cascades correctly, so className styles overrides .button, then remove this NONE variant
  NONE = 'none',
}

export interface ButtonProps {
  ariaLabel: string;
  blank?: boolean;
  children: ReactNode;
  className?: string;
  href?: string;
  id?: string;
  isDisabled?: boolean;
  mixpanelEvent?: MixpanelTrackEvent;
  mixpanelEventValues?: ObjectOfAny;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  testId?: string;
  title?: string;
  type?: React.ComponentProps<'button'>['type'];
  variant: ButtonVariant;
}

export type Props = RequireAtLeastOne<ButtonProps, 'onClick' | 'href'>;

function Button(props: Props): ReactElement {
  const {
    ariaLabel,
    blank,
    children,
    className,
    href,
    id,
    isDisabled,
    mixpanelEvent,
    mixpanelEventValues,
    onClick = () => null,
    testId,
    title,
    type,
    variant,
  } = props;

  function getVariantClassName() {
    switch (variant) {
      case ButtonVariant.PRIMARY:
        return 'button button--primary';
      case ButtonVariant.PRIMARY_OUTLINE:
        return 'button button--primary--outline';
      case ButtonVariant.CALL_TO_ACTION:
        return 'button button--callToAction';
      case ButtonVariant.CALL_TO_ACTION_OUTLINE:
        return 'button button--callToAction--outline';
      case ButtonVariant.DEFAULT:
        return 'button button--default';
      case ButtonVariant.DEFAULT_OUTLINE:
        return 'button button--default--outline';
      case ButtonVariant.DEFAULT_TEXT:
        return 'button button--basic';
      case ButtonVariant.LINK_TEXT:
        return 'button button--link';
      case ButtonVariant.NEGATIVE:
        return 'button button--negative';
      case ButtonVariant.SUCCESS:
        return 'button button--success';
      case ButtonVariant.UNSTYLED:
        return 'button--unstyled';
      case ButtonVariant.NONE:
        return '';
      default:
        return 'button button--primary';
    }
  }

  const buttonOnClick = (event: React.MouseEvent<HTMLElement>) => {
    if (mixpanelEvent) {
      trackEvent({
        name: mixpanelEvent,
        eventType: EventType.MIXPANEL,
        values: mixpanelEventValues,
      });
    }

    onClick(event);
  };

  if (href) {
    return (
      <DynamicLink
        href={href}
        className={`${getVariantClassName()} ${className || ''}`.trim()}
        aria-label={ariaLabel}
        title={title}
        testId={testId || id}
        blank={blank}
        onClick={buttonOnClick}
      >
        {children}
      </DynamicLink>
    );
  }
  /* eslint-disable react/button-has-type */
  return (
    <button
      className={`${getVariantClassName()} ${className || ''}`.trim()}
      aria-label={ariaLabel}
      title={title}
      data-testid={testId || id}
      disabled={isDisabled}
      id={id}
      onClick={buttonOnClick}
      type={type}
    >
      {children}
    </button>
  );
}

export default Button;
